import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import GoogleMapReact from "google-map-react";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import API_ENDPOINTS from "../apiConfig";
import TeamMapPin from "../components/TeamMapPin";
import TeamSchedule from "../components/TeamSchedule";
import { useSeason } from "../components/SeasonContext";
import { Team } from "../interfaces";
import { getAppSurfaceStyles } from "../theme/siteTheme";

const MAP_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || "";

const TeamDetailPage: React.FC = () => {
  // Get the id from location.state (passed from TeamsPage)
  const location = useLocation();
  const navigate = useNavigate();
  const id = location.state?.id;
  const [team, setTeam] = useState<Team | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [imageOpen, setImageOpen] = useState(false);
  const { selectedSeason } = useSeason();
  const theme = useTheme();
  const styles = getAppSurfaceStyles(theme);
  const isMobile = window.innerWidth < 600;

  useEffect(() => {
    if (!id) {
      navigate("/teams"); // redirect to teams list if no id is present
      return;
    }
    setLoading(true);
    (async () => {
      try {
        const url = API_ENDPOINTS.team(id);
        const res = await fetch(url);
        if (!res.ok) throw new Error("Team nicht gefunden");
        const data = await res.json();
        setTeam(data);
        setError(null);
      } catch (err: any) {
        setError(err.message);
        setTeam(null);
      } finally {
        setLoading(false);
      }
    })();
  }, [id, navigate]);

  if (loading)
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <CircularProgress />
      </Box>
    );
  if (error)
    return <Box sx={{ p: 4, textAlign: "center", color: "red" }}>{error}</Box>;
  if (!team) return null;

  // Robust coordinate check
  const hasValidCoords =
    !loading &&
    team.spielort &&
    typeof team.spielort.lat === "number" &&
    typeof team.spielort.lng === "number" &&
    team.spielort.lat !== null &&
    team.spielort.lng !== null &&
    !isNaN(team.spielort.lat) &&
    !isNaN(team.spielort.lng) &&
    team.spielort.lat !== undefined &&
    team.spielort.lng !== undefined;

  const coords = hasValidCoords
    ? { lat: team.spielort!.lat as number, lng: team.spielort!.lng as number }
    : { lat: 48.5, lng: 9.0 };

  // Only render TeamMapPin if both lat/lng are valid finite numbers
  const renderMapPin =
    hasValidCoords && isFinite(coords.lat) && isFinite(coords.lng);

  // Social icons (same SVGs as TeamCard/Footer)
  const socialIcons: Record<string, React.ReactNode> = {
    facebook: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor">
        <path d="M22.675 0h-21.35C.595 0 0 .592 0 1.326v21.348C0 23.406.595 24 1.325 24h11.495v-9.294H9.692v-3.622h3.128V8.413c0-3.1 1.893-4.788 4.659-4.788 1.325 0 2.463.099 2.797.143v3.24l-1.918.001c-1.504 0-1.797.715-1.797 1.763v2.313h3.587l-.467 3.622h-3.12V24h6.116C23.406 24 24 23.406 24 22.674V1.326C24 .592 23.406 0 22.675 0" />
      </svg>
    ),
    instagram: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 1.366.062 2.633.334 3.608 1.308.974.974 1.246 2.241 1.308 3.608.058 1.266.07 1.646.07 4.85s-.012 3.584-.07 4.85c-.062 1.366-.334 2.633-1.308 3.608-.974.974-2.241 1.246-3.608 1.308-1.266.058-1.646.07-4.85.07s-3.584-.012-4.85-.07c-1.366-.062-2.633-.334-3.608-1.308-.974-.974-1.246-2.241-1.308-3.608C2.175 15.647 2.163 15.267 2.163 12s.012-3.584.07-4.85c.062-1.366.334-2.633 1.308-3.608.974-.974 2.241-1.246 3.608-1.308C8.416 2.175 8.796 2.163 12 2.163zm0-2.163C8.741 0 8.332.013 7.052.072 5.775.131 4.602.425 3.635 1.392 2.668 2.359 2.374 3.532 2.315 4.808 2.256 6.088 2.243 6.497 2.243 12c0 5.503.013 5.912.072 7.192.059 1.276.353 2.449 1.32 3.416.967.967 2.14 1.261 3.416 1.32 1.28.059 1.689.072 7.192.072s5.912-.013 7.192-.072c1.276-.059 2.449-.353 3.416-1.32.967-.967 1.261-2.14 1.32-3.416.059-1.28.072-1.689.072-7.192s-.013-5.912-.072-7.192c-.059-1.276-.353-2.449-1.32-3.416C21.449.425 20.276.131 19 .072 17.72.013 17.311 0 12 0zm0 5.838a6.162 6.162 0 1 0 0 12.324 6.162 6.162 0 0 0 0-12.324zm0 10.162a3.999 3.999 0 1 1 0-7.998 3.999 3.999 0 0 1 0 7.998zm6.406-11.845a1.44 1.44 0 1 0 0 2.881 1.44 1.44 0 0 0 0-2.881z" />
      </svg>
    ),
    website: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 2C6.477 2 2 6.477 2 12s4.477 10 10 10 10-4.477 10-10S17.523 2 12 2zm0 2c1.657 0 3.156.672 4.242 1.758A5.978 5.978 0 0 1 19.584 9H16.5a.5.5 0 0 0 0 1h3.082c.027.329.043.662.043 1s-.016.671-.043 1H16.5a.5.5 0 0 0 0 1h3.084a5.978 5.978 0 0 1-3.342 3.242A7.963 7.963 0 0 1 12 20a7.963 7.963 0 0 1-4.242-1.758A5.978 5.978 0 0 1 4.416 15H7.5a.5.5 0 0 0 0-1H4.418A7.963 7.963 0 0 1 4 13c0-.338.016-.671.043-1H7.5a.5.5 0 0 0 0-1H4.416a5.978 5.978 0 0 1 3.342-3.242A7.963 7.963 0 0 1 12 4zm0 2a6 6 0 1 0 0 12A6 6 0 0 0 12 6z" />
      </svg>
    ),
  };

  return (
    <Box
      sx={{ ...styles.pageContainer, maxWidth: 700 }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: "center",
          mb: 3,
          gap: 3,
        }}
      >
        {team.image ? (
          <>
            <Box
              component="img"
              src={team.image}
              alt={team.name}
              sx={styles.heroImage}
              onClick={() => setImageOpen(true)}
            />
            {/* Dialog for large image */}
            <Box>
              <Dialog
                open={imageOpen}
                onClose={() => setImageOpen(false)}
                maxWidth="md"
              >
                <Box sx={styles.dialogSurface}>
                  <Box
                    component="img"
                    src={team.image}
                    alt={team.name}
                    sx={styles.dialogImage}
                  />
                  <Box sx={{ textAlign: "center", mt: 1 }}>
                    <Typography variant="subtitle1" sx={styles.dialogCaption}>
                      {team.name}
                    </Typography>
                  </Box>
                </Box>
              </Dialog>
            </Box>
          </>
        ) : null}
        <Box sx={{ flex: 1, textAlign: { xs: "center", sm: "left" } }}>
          <Typography
            variant="h2"
            sx={{
              mb: 1,
              fontWeight: 700,
              fontSize: { xs: 28, sm: 36 },
              color: "text.primary",
            }}
          >
            {team.name}
          </Typography>
        </Box>
      </Box>
      {team.description && team.description.title && (
        <Typography variant="h5" sx={styles.sectionTitle}>
          {team.description.title}
        </Typography>
      )}
      {team.description &&
        Array.isArray(team.description.paragraphs) &&
        team.description.paragraphs.length > 0 && (
          <Box sx={{ mb: 2 }}>
            {team.description.paragraphs.map((para: string, idx: number) => (
              <Typography key={idx} sx={styles.sectionText}>
                {para}
              </Typography>
            ))}
          </Box>
        )}
      
      {/* Team Schedule Section - moved above Spielort */}
      {selectedSeason && team.id && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" sx={{ ...styles.sectionTitle, mb: 2 }}>
            Spielplan
          </Typography>
          <TeamSchedule 
            teamId={team.id} 
            seasonId={Number(selectedSeason)} 
            isMobile={isMobile}
          />
        </Box>
      )}
      
      {team.spielort && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="h5" sx={styles.sectionTitle}>
            Spielort
          </Typography>
          <Typography sx={styles.sectionText}>
            <strong>Name:</strong> {team.spielort.name}
          </Typography>
          <Typography sx={styles.sectionText}>
            <strong>Adresse:</strong> {team.spielort.street}
            {", "}
            {team.spielort.plzOrt}
          </Typography>
          {team.spielort.link && (
            <Typography sx={styles.sectionText}>
              <Box
                component="a"
                href={team.spielort.link}
                target="_blank"
                rel="noopener noreferrer"
                sx={styles.bodyLink}
              >
                Zum Spielort (Website)
              </Box>
              {" | "}
              <Box
                component="a"
                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                  team.spielort.name +
                    ", " +
                    team.spielort.street +
                    ", " +
                    team.spielort.plzOrt
                )}`}
                target="_blank"
                rel="noopener noreferrer"
                sx={styles.bodyLink}
              >
                Google Maps
              </Box>
            </Typography>
          )}
        </Box>
      )}
      <Box
        sx={{ ...styles.mapFrame, height: 320, mb: 3 }}
      >
        <GoogleMapReact
          bootstrapURLKeys={{ key: MAP_API_KEY }}
          defaultCenter={coords}
          defaultZoom={12}
          distanceToMouse={() => 0}
        >
          {renderMapPin && (
            <TeamMapPin team={team} lat={coords.lat} lng={coords.lng} />
          )}
        </GoogleMapReact>
      </Box>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h5" sx={styles.sectionTitle}>
          Kontakt & Kapitän
        </Typography>
        <Typography sx={styles.sectionText}>
          <strong>Kapitän:</strong> {team.kapitaen}
        </Typography>
        {team.links && team.links.length > 0 && (
          <Box
            sx={{
              display: "flex",
              gap: 2,
              mt: 1,
              justifyContent: {
                xs: "center",
                sm: "flex-start",
              },
            }}
          >
            {team.links.map((link) => (
              <Tooltip
                key={link.url}
                title={
                  link.link_type.charAt(0).toUpperCase() +
                  link.link_type.slice(1)
                }
                arrow
              >
                <Box
                  component="a"
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={styles.socialLink}
                >
                  {socialIcons[link.link_type]}
                </Box>
              </Tooltip>
            ))}
          </Box>
        )}
        {/* {team.kapitaenMobil && (
          <Typography sx={{ mb: 0.5 }}>
            <strong>Mobil:</strong> {team.kapitaenMobil}
          </Typography>
        )} */}
        {/* {team.telefon && (
          <Typography sx={{ mb: 0.5 }}>
            <strong>Telefon:</strong> {team.telefon}
          </Typography>
        )} */}
        {/* {team.email && (
          <Typography sx={{ mb: 0.5 }}>
            <strong>E-Mail:</strong>{" "}
            <a href={`mailto:${team.email}`}>{team.email}</a>
          </Typography>
        )} */}
      </Box>
      {team.spielbeginn && (
        <Typography sx={{ ...styles.sectionText, mb: 2 }}>
          <strong>Spielbeginn:</strong> {team.spielbeginn}
        </Typography>
      )}
    </Box>
  );
};

export default TeamDetailPage;
